import { headers } from './constants';
import Identity from './identity';
import JSONResponseHandler from '../serviceHelpers/JSONResponseHandler';
import { APIErrorResponse, isAPIErrorResponse } from '../serviceHelpers/APIErrorResponse';

export interface ConfigOptions {
  signupRecaptcha: boolean;
  signinRecaptcha: boolean;
  magicLinkRecaptcha: boolean;
  resetPasswordRecaptcha: boolean;
  googleClientId: string;
  facebookAppId: string;
  disqus?: {
    enabled: boolean;
    publicKey: string;
  };
}

/** @hidden */
export function isConfigOptions(object: ConfigOptions | APIErrorResponse): object is ConfigOptions {
  return (<ConfigOptions>object).signinRecaptcha !== undefined;
}

/**
 * Gets configuration options such as recaptcha being on or off.
 * These config options are generally set up in the Subs Administration portal.
 * This method will populate the __Identity.configOptions__ property.
 *
 * @returns
 * A __Promise__ which resolves to a JSON object with all available config options
 *
 * <details>
 * <summary>
 * On success (resolves)
 * </summary>
 *
 * ```json
 * {
 *   "signupRecaptcha": false,
 *   "signinRecaptcha": false,
 *   "magicLinkRecaptcha": false,
 *   "googleClientId": null,
 *   "facebookAppId": "320818148787173"
 * }
 * ```
 * </details>
 *
 * <details>
 * <summary>
 * On errors (throws)
 * </summary>
 *
 * ```
 * See documentation for corresponding error codes:
 * https://redirector.arcpublishing.com/alc/docs/swagger/?url=./arc-products/arc-identity-v1.json#/Tenant_Configuration/get
 * ```
 * </details>
 **/
export async function getConfig(): Promise<ConfigOptions | APIErrorResponse> {
  if (!Identity._configOptions) {
    return fetch(`${Identity.apiOrigin}/identity/public/v1/config`, {
      method: 'GET',
      cache: 'no-cache',
      headers: {
        ...headers
      }
    })
      .then(JSONResponseHandler)
      .then(json => {
        if (isAPIErrorResponse(json)) {
          throw json;
        } else {
          Identity._configOptions = json;
          return json;
        }
      });
  } else {
    return Identity._configOptions;
  }
}

export default getConfig;
